import React, { useEffect, useState } from "react";
import "../assets/AllTabsCommon.css";
import "../assets/StartRemoteDebug.css";
import Select from "react-select";
import { fetchData, postData } from "../api/restapi";

const isVersionAtLeast2 = (version) => {
  if (!version) return false;
  const major = parseInt(version.split('.')[0], 10);
  return !isNaN(major) && major >= 2;
};

const timeToSecondsFromDayStart = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return ((h || 0) * 3600) + ((m || 0) * 60);
};

const computeDayMask = (activeDays) => {
  const bits = { mon: 1, tue: 2, wed: 4, thu: 8, fri: 16, sat: 32, sun: 64 };
  return Object.entries(bits).reduce((mask, [d, b]) => activeDays[d] ? mask | b : mask, 0);
};

const secondsToTime = (secs) => {
  if (!secs && secs !== 0) return '';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const dayMaskToActiveDays = (mask) => {
  const bits = { mon: 1, tue: 2, wed: 4, thu: 8, fri: 16, sat: 32, sun: 64 };
  return Object.fromEntries(Object.entries(bits).map(([d, b]) => [d, Boolean(mask & b)]));
};

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const emptySchedule = () => ({
  applyToRules: [],
  activeDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
  startTime: '',
  durationMins: '',
  windowDuration: '',
});

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    height: '38px',
    borderColor: 'rgb(103, 114, 132)',
  }),
  valueContainer: (base) => ({
    ...base,
    height: '38px',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '38px',
  }),
  placeholder: (base) => ({
    ...base,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 0,
  }),
  singleValue: (base) => ({
    ...base,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 0,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
};

const selectStylesMulti = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    height: '38px',
    borderColor: 'rgb(103, 114, 132)',
  }),
  valueContainer: (base) => ({
    ...base,
    height: '38px',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '38px',
  }),
};

function UpdateSessionComponent({ session, userInfo, collectorNames, onGoBack }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [rows, setRows] = useState([]);
  const [initialRows, setInitialRows] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [validationError, setValidationError] = useState("");
  const [diagnosticDataOptions, setDiagnosticDataOptions] = useState([]);
  const [logMaskDescriptions, setLogMaskDescriptions] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Open ended
  const [isOpenEnded, setIsOpenEnded] = useState(false);
  const [isOpenEndedDisabled, setIsOpenEndedDisabled] = useState(true);
  const [initialIsOpenEnded, setInitialIsOpenEnded] = useState(false);

  // Context collection
  const [contextCollectionRuleOptions, setContextCollectionRuleOptions] = useState([]);
  const [maxFileSizeMb, setMaxFileSizeMb] = useState("");
  const [contextRules, setContextRules] = useState([]);
  const [collectionSchedule, setCollectionSchedule] = useState(emptySchedule());

  // Session-level Log Mask (mandatory for ODL + CONFIGURE_ENABLE/RE_CONFIGURE, all device versions)
  const [sessionLogMask, setSessionLogMask] = useState(null);

  // Initial ODL config values — pre-populated from the session's stored state for change detection
  const [initialLogMask, setInitialLogMask] = useState(null);
  const [initialMaxFileSizeMb, setInitialMaxFileSizeMb] = useState("");
  const [initialContextRules, setInitialContextRules] = useState([]);
  const [initialCollectionSchedule, setInitialCollectionSchedule] = useState(emptySchedule());

  const COMMAND_OPTIONS_ODL = [
    { value: "CONFIGURE_ENABLE", label: "CONFIGURE_ENABLE" },
    { value: "RE_CONFIGURE", label: "RE_CONFIGURE" },
    { value: "ENABLE", label: "ENABLE" },
    { value: "DISABLE", label: "DISABLE" },
  ];

  const COMMAND_OPTIONS_DEFAULT = [
    { value: "ENABLE", label: "ENABLE" },
    { value: "DISABLE", label: "DISABLE" },
  ];

  const areArraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
      const item1 = arr1[i];
      const item2 = arr2[i];
      if (item1.diagnosticData?.value !== item2.diagnosticData?.value || item1.diagnosticData?.label !== item2.diagnosticData?.label) return false;
      if (item1.command?.value !== item2.command?.value || item1.command?.label !== item2.command?.label) return false;
      const item1IsOdlConfigurable = item1.diagnosticData?.label === "ODL" && (item1.command?.value === "CONFIGURE_ENABLE" || item1.command?.value === "RE_CONFIGURE");
      const item2IsOdlConfigurable = item2.diagnosticData?.label === "ODL" && (item2.command?.value === "CONFIGURE_ENABLE" || item2.command?.value === "RE_CONFIGURE");
      if (item1IsOdlConfigurable && item2IsOdlConfigurable) {
        const maxFileSize1 = item1.maxFileSizeMb === "" ? null : item1.maxFileSizeMb;
        const maxFileSize2 = item2.maxFileSizeMb === "" ? null : item2.maxFileSizeMb;
        if (maxFileSize1 !== maxFileSize2) return false;
      } else if (item1IsOdlConfigurable !== item2IsOdlConfigurable) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    const fetchDiagnosticData = async () => {
      try {
        const data = await fetchData("/api/v1/diagnostic-data");
        if (data.statusCode === 200) {
          const options = data.collectorIds.map((collector) => ({
            value: collector.id,
            label: collector.name,
          }));
          setDiagnosticDataOptions(options);

          const dataToInitialize = session.currentDiagnosticData || session.collectorExecutionStatuses;
          const initialSessionRows = Array.isArray(dataToInitialize)
            ? dataToInitialize.map(status => {
                const matchedOption = options.find(opt => opt.value === status.collectorId);
                const commandLabel = COMMAND_OPTIONS_ODL.find(cmd => cmd.value === status.mqttCommandName)?.label ||
                                     COMMAND_OPTIONS_DEFAULT.find(cmd => cmd.value === status.mqttCommandName)?.label ||
                                     status.mqttCommandName;
                return {
                  diagnosticData: matchedOption || null,
                  command: { value: status.mqttCommandName, label: commandLabel },
                  selectedLogMask: null,
                  // Pre-populate per-row maxFileSizeMb for V1 ODL rows from session
                  maxFileSizeMb: (matchedOption?.label === "ODL" && session.maxFileSizeMb != null)
                    ? String(session.maxFileSizeMb) : "",
                  isInitial: true,
                };
              })
            : [];

          setRows(initialSessionRows);
          setInitialRows(initialSessionRows);
        }
      } catch (err) {}
    };

    const fetchLogMaskDescriptions = async () => {
      try {
        const data = await fetchData("/api/v1/onboarding/log-mask-descriptions");
        if (data.logMaskDescriptions) {
          const options = data.logMaskDescriptions.map((mask) => ({
            value: mask.id,
            label: mask.description,
            logCodesIndex: mask.logCodesIndex,
          }));
          setLogMaskDescriptions(options);

          // Pre-populate log mask from session's stored logMaskId
          if (session?.logMaskId) {
            const matched = options.find(o => o.value === session.logMaskId);
            if (matched) {
              setSessionLogMask(matched);
              setInitialLogMask(matched);
            }
          }
        }
      } catch (err) {}
    };

    const fetchContextCollectionRules = async () => {
      try {
        const data = await fetchData("/api/v1/onboarding/context-collection-rules");
        if (data.rules) {
          const options = data.rules.map((rule) => ({
            value: rule.id,
            label: `${rule.label} (${rule.ruleType})`,
          }));
          setContextCollectionRuleOptions(options);
        }
      } catch (err) {}
    };

    const checkSystemVersion = async () => {
      if (!session?.systemId) return;
      try {
        const statusResponse = await fetchData(`/api/v1/device-current-status/${session.systemId}`);
        if (statusResponse.statusCode === 200) {
          const version = statusResponse.currentClientStatus?.version;
          if (isVersionAtLeast2(version)) {
            setIsOpenEndedDisabled(false);
          } else {
            setIsOpenEndedDisabled(true);
          }
        }
      } catch (err) {
        setIsOpenEndedDisabled(true);
      }
    };

    if (session) {
      const sessionOpenEnded = session.isOpenEnded || false;
      setIsOpenEnded(sessionOpenEnded);
      setInitialIsOpenEnded(sessionOpenEnded);

      // Pre-populate session-level maxFileSizeMb (used by V2 flow; V1 uses per-row value)
      if (session.maxFileSizeMb != null) {
        const val = String(session.maxFileSizeMb);
        setMaxFileSizeMb(val);
        setInitialMaxFileSizeMb(val);
      }

      fetchDiagnosticData();
      fetchLogMaskDescriptions();
      fetchContextCollectionRules();
      checkSystemVersion();
    }
  }, [session]);

  // Pre-populate contextRules and collectionSchedule from session JSON once both option arrays are ready
  useEffect(() => {
    if (!session || contextCollectionRuleOptions.length === 0 || logMaskDescriptions.length === 0) return;

    if (session.ruleConfigurations) {
      try {
        const parsedRules = JSON.parse(session.ruleConfigurations);
        if (Array.isArray(parsedRules) && parsedRules.length > 0) {
          const mapped = parsedRules.map(r => ({
            contextCollectionRule: contextCollectionRuleOptions.find(o => o.value === r.contextCollectionRuleId) || null,
            diagConfig: logMaskDescriptions.find(o => o.logCodesIndex === r.logCodesIndex) || null,
          }));
          setContextRules(mapped);
          setInitialContextRules(mapped);
        }
      } catch (e) {}
    }

    if (session.collectionSchedules) {
      try {
        const parsed = JSON.parse(session.collectionSchedules);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const s = parsed[0];
          const schedule = {
            applyToRules: (s.contextCollectionRuleIds || [])
              .map(id => contextCollectionRuleOptions.find(o => o.value === id))
              .filter(Boolean),
            activeDays: dayMaskToActiveDays(s.dayMask || 0),
            startTime: secondsToTime(s.startTime),
            durationMins: s.duration ? String(Math.round(s.duration / 60)) : '',
            windowDuration: s.windowDuration ? String(Math.round(s.windowDuration / 60)) : '',
          };
          setCollectionSchedule(schedule);
          setInitialCollectionSchedule(schedule);
        }
      } catch (e) {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextCollectionRuleOptions, logMaskDescriptions]);

  useEffect(() => {
    if (initialRows.length > 0 || rows.length > 0) {
      const rowChanged = !areArraysEqual(rows, initialRows);

      // ODL config changes are only meaningful when RE_CONFIGURE is the active command
      const odlReConfigureActive = rows.some(
        row => row.diagnosticData?.label === "ODL" && row.command?.value === "RE_CONFIGURE"
      );

      const logMaskChanged = odlReConfigureActive &&
        sessionLogMask?.value !== initialLogMask?.value;

      const maxFileSizeChanged = odlReConfigureActive && !isOpenEndedDisabled &&
        maxFileSizeMb !== initialMaxFileSizeMb;

      const serializeRules = (rules) => JSON.stringify(
        rules.filter(r => r.contextCollectionRule).map(r => ({
          ruleId: r.contextCollectionRule?.value,
          logCodesIndex: r.diagConfig?.logCodesIndex,
        }))
      );
      const rulesChanged = odlReConfigureActive && !isOpenEndedDisabled &&
        serializeRules(contextRules) !== serializeRules(initialContextRules);

      const scheduleChanged = odlReConfigureActive && !isOpenEndedDisabled &&
        JSON.stringify(collectionSchedule) !== JSON.stringify(initialCollectionSchedule);

      setHasChanges(rowChanged || logMaskChanged || maxFileSizeChanged || rulesChanged || scheduleChanged);
    }
  }, [rows, initialRows, sessionLogMask, initialLogMask, maxFileSizeMb, initialMaxFileSizeMb,
      contextRules, initialContextRules, collectionSchedule, initialCollectionSchedule, isOpenEndedDisabled]);

  const resetContextCollection = () => {
    setMaxFileSizeMb("");
    setContextRules([]);
    setCollectionSchedule(emptySchedule());
    setSessionLogMask(null);
  };

  const isFormValid = () => {
    // Get only the rows that have changed
    const changedRows = rows.filter(row => {
      if (!row.diagnosticData || !row.command) return false;
      
      const initialRow = initialRows.find(
        initial => initial.diagnosticData?.value === row.diagnosticData?.value
      );
      
      // New row (not in initial)
      if (!initialRow) return true;

      // Command changed
      if (initialRow.command?.value !== row.command?.value) return true;

      // RE_CONFIGURE rows are always included: the user can update ODL config without changing the command
      if (row.diagnosticData?.label === "ODL" && row.command?.value === "RE_CONFIGURE") return true;

      return false;
    });
    
    // If no changes, form is invalid
    if (changedRows.length === 0) return false;
    
    // Validate only the changed rows
    return changedRows.every((row) => {
      if (!row.diagnosticData || !row.command) return false;
      
      // ODL with CONFIGURE_ENABLE or RE_CONFIGURE
      if (row.diagnosticData.label === "ODL" &&
          (row.command.value === "CONFIGURE_ENABLE" || row.command.value === "RE_CONFIGURE")) {

        // Session-level Log Mask is always required for CONFIGURE_ENABLE / RE_CONFIGURE
        if (!sessionLogMask) return false;

        // For v2.0+ devices: validate context collection (new flow)
        if (!isOpenEndedDisabled) {
          return maxFileSizeMb.trim() !== "" &&
            // Rules are optional, but if provided, they must be complete
            contextRules.every(r => r.contextCollectionRule && r.diagConfig);
        }

        // For older devices: validate legacy Max File Size field
        return row.maxFileSizeMb.trim() !== "";
      }
      
      // For other collectors/commands, just having them selected is valid
      return true;
    });
  };

  const buildContextCollectionPayload = () => {
    const payload = {
      odlMode: "ODL_CONTEXT_COLLECTION",
      maxFileSizeMb: parseInt(maxFileSizeMb),
      ruleConfigurations: contextRules
        .filter(r => r.contextCollectionRule && r.diagConfig)
        .map(r => ({
          contextCollectionRuleId: r.contextCollectionRule.value,
          logCodesIndex: r.diagConfig.logCodesIndex,
        })),
    };

    const hasSchedule =
      collectionSchedule.startTime !== '' ||
      collectionSchedule.durationMins !== '' ||
      collectionSchedule.windowDuration !== '' ||
      collectionSchedule.applyToRules.length > 0 ||
      Object.values(collectionSchedule.activeDays).some(v => v);

    if (hasSchedule) {
      payload.collectionSchedules = [{
        contextCollectionRuleIds: collectionSchedule.applyToRules.map(r => r.value),
        startTime: timeToSecondsFromDayStart(collectionSchedule.startTime),
        duration: parseInt(collectionSchedule.durationMins || 0) * 60,
        dayMask: computeDayMask(collectionSchedule.activeDays),
        windowDuration: parseInt(collectionSchedule.windowDuration || 0) * 60,
      }];
    }
    return payload;
  };

  const handleContinue = () => {
    setValidationError("");
    setCurrentStep(2);
  };

  const handleGoBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      onGoBack();
    }
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleSubmit = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    const changedRows = rows.filter(row => {
      if (!row.diagnosticData || !row.command) return false;
      const initialRow = initialRows.find(initial => initial.diagnosticData?.value === row.diagnosticData?.value);
      if (!initialRow) return true;
      if (initialRow.command?.value !== row.command?.value) return true;
      if (row.diagnosticData?.label === "ODL" && row.command?.value === "RE_CONFIGURE") return true;
      return false;
    });

    const requestBody = {
      systemId: session.systemId,
      sessionId: session.sessionId,
      sessionType: "WBC_UPDATE_SESSION",
      wbcCollectorCommandNameMappings: changedRows.map((row) => ({
        collectorId: row.diagnosticData.value,
        commandName: row.command.value === "RE_CONFIGURE" ? "CONFIGURE_ENABLE" : row.command.value,
      })),
      sessionTimeInMins: parseInt(session.sessionTimeInMins || 0),
      uploadReportFreqInSec: parseInt(session.uploadReportFreqInSec || 0),
      user: userInfo,
      isOpenEnded: isOpenEnded,
    };

    const odlRow = changedRows.find(row => row.diagnosticData?.label === "ODL");

    if (odlRow) {
      if (odlRow.command?.value === "CONFIGURE_ENABLE" || odlRow.command?.value === "RE_CONFIGURE") {
        if (!isOpenEndedDisabled) {
          // V2 → ODL_CONTEXT_COLLECTION
          Object.assign(requestBody, buildContextCollectionPayload());
          requestBody.logMaskId = sessionLogMask?.value;
        } else {
          // V1 → ODL_LOG_MASK
          requestBody.odlMode = "ODL_LOG_MASK";
          requestBody.logMaskId = sessionLogMask?.value;
          requestBody.maxFileSizeMb = parseInt(odlRow.maxFileSizeMb);
          requestBody.ruleConfigurations = [];  // Explicitly clears any existing rules
          requestBody.collectionSchedules = []; // Explicitly clears any existing schedules
        }
      } else if (!isOpenEndedDisabled) {
        // ENABLE or DISABLE command on V2 device — ODL_CONTEXT_COLLECTION for Proxy ODL auto-provisioning
        requestBody.odlMode = "ODL_CONTEXT_COLLECTION";
      }
    }

    try {
      const response = await postData("/api/v1/configure-diagnostics", requestBody);
      if (response.statusCode === 200) {
        setSuccessMessage(`Session ${session.sessionId} updated successfully!`);
      } else {
        setErrorMessage(response.errorMessage || `Failed to update session ${session.sessionId}.`);
      }
    } catch (error) {
      setErrorMessage(`Failed to update session ${session.sessionId}: ${error.message}`);
    }
  };

  const handleAddRow = () => {
    if (getRemainingOptionsCount() > 0) {
      setRows([...rows, { diagnosticData: null, selectedLogMask: null, maxFileSizeMb: "", command: null, isInitial: false }]);
    }
  };

  const handleRemoveRow = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);
  };

  const handleRowChange = (index, field, value) => {
    const oldRow = rows[index];
    const updatedRow = { ...oldRow, [field]: value };

    const wasContextCollection = oldRow.diagnosticData?.label === "ODL" && oldRow.command?.value === "CONFIGURE_ENABLE";
    const isContextCollection = updatedRow.diagnosticData?.label === "ODL" && updatedRow.command?.value === "CONFIGURE_ENABLE";

    if (wasContextCollection && !isContextCollection) {
      resetContextCollection();
    }

    setRows(rows.map((row, i) => i === index ? updatedRow : row));
  };

  const getFilteredOptions = (index) => {
    const selectedValues = rows
      .map((row, i) => (i !== index ? row.diagnosticData?.value : null))
      .filter((value) => value !== null);
    return diagnosticDataOptions.filter((option) => !selectedValues.includes(option.value));
  };

  const getRemainingOptionsCount = () => {
    const selectedValues = rows.map((row) => row.diagnosticData?.value).filter((value) => value !== null);
    return diagnosticDataOptions.filter((option) => !selectedValues.includes(option.value)).length;
  };

  const addContextRule = () => {
    setContextRules([...contextRules, { contextCollectionRule: null, diagConfig: null }]);
  };

  const removeContextRule = (index) => {
    const removed = contextRules[index];
    const updated = contextRules.filter((_, i) => i !== index);
    setContextRules(updated);
    if (removed?.contextCollectionRule) {
      setCollectionSchedule(prev => ({
        ...prev,
        applyToRules: prev.applyToRules.filter(r => r.value !== removed.contextCollectionRule.value),
      }));
    }
  };

  const handleContextRuleChange = (index, field, value) => {
    const updated = contextRules.map((r, i) => i === index ? { ...r, [field]: value } : r);
    setContextRules(updated);
  };

  const handleScheduleChange = (field, value) => {
    setCollectionSchedule(prev => ({ ...prev, [field]: value }));
  };

  const scheduleRuleOptions = contextRules
    .filter(r => r.contextCollectionRule)
    .map(r => ({ value: r.contextCollectionRule.value, label: r.contextCollectionRule.label }));

  if (currentStep === 1) {
    return (
      <div className="content-tab-container">
        <div className="tab-heading">
          <h1>Update Remote Diagnostics Management Session</h1>
        </div>

        {validationError && (
          <div className="error-banner">
            <div className="error-message">{validationError}</div>
          </div>
        )}

        <div className="row">
          <div className="dropdown-section">
            <div className="dropdown-heading">System ID</div>
            <input type="text" value={session.systemId} readOnly disabled />
          </div>
          <div className="dropdown-section">
            <div className="dropdown-heading">Session ID</div>
            <input type="text" value={session.sessionId} readOnly disabled />
          </div>
        </div>

        <div className="spacing"></div>
        {rows.map((row, index) => (
          <div key={index}>
            <div className="row">
              <div className="dropdown-section">
                <div className="dropdown-heading">Diagnostic Data</div>
                <Select
                  className="dropdown multi-select"
                  styles={selectStyles}
                  options={getFilteredOptions(index)}
                  value={row.diagnosticData}
                  onChange={(value) => handleRowChange(index, "diagnosticData", value)}
                  placeholder="Select..."
                  isDisabled={row.isInitial}
                />
              </div>
              <div className="dropdown-section">
                <div className="dropdown-heading">Command</div>
                <Select
                  className="dropdown multi-select"
                  styles={selectStyles}
                  options={row.diagnosticData?.label === "ODL" ? COMMAND_OPTIONS_ODL : COMMAND_OPTIONS_DEFAULT}
                  value={row.command}
                  onChange={(value) => handleRowChange(index, "command", value)}
                  placeholder="Select..."
                />
              </div>
              {getRemainingOptionsCount() > 0 && (
                <button className="add-row-button" style={{ alignSelf: 'flex-end', margin: '0' }} onClick={handleAddRow}>
                  + Add Diagnostic Data
                </button>
              )}
              {rows.length > 1 && !row.isInitial && (
                <button className="add-row-button" style={{ alignSelf: 'flex-end', margin: '0' }} onClick={() => handleRemoveRow(index)}>
                  - Remove Diagnostic Data
                </button>
              )}
            </div>

            {/* Diag Config — shown only when command is RE_CONFIGURE, or a new row with CONFIGURE_ENABLE.
                Never shown when an existing CONFIGURE_ENABLE row stays CONFIGURE_ENABLE (use RE_CONFIGURE to update ODL config). */}
            {row.diagnosticData?.label === "ODL" &&
             (row.command?.value === "CONFIGURE_ENABLE" || row.command?.value === "RE_CONFIGURE") &&
             (() => {
               const initialRow = initialRows.find(initial => initial.diagnosticData?.value === row.diagnosticData?.value);
               const isReconfigure = row.command?.value === "RE_CONFIGURE";
               const commandChanged = !initialRow || initialRow.command?.value !== row.command?.value;
               return isReconfigure || commandChanged;
             })() && (
              <div className="row odl-config-row" style={{ marginBottom: '10px' }}>
                <div className="dropdown-section">
                  <div className="dropdown-heading">
                    Diag Config<span className="required-asterisk">*</span>
                  </div>
                  <Select
                    className="dropdown multi-select"
                    styles={selectStyles}
                    options={logMaskDescriptions}
                    value={sessionLogMask}
                    onChange={(val) => setSessionLogMask(val)}
                    placeholder="Select..."
                  />
                </div>
              </div>
            )}

            {/* ODL CONFIGURE_ENABLE or RE_CONFIGURE (for v2.0+ devices ONLY): show context collection UI only if changed */}
            {row.diagnosticData?.label === "ODL" &&
             !isOpenEndedDisabled &&  // V2 device check
             (row.command?.value === "CONFIGURE_ENABLE" || row.command?.value === "RE_CONFIGURE") &&
             (() => {
               const initialRow = initialRows.find(
                 initial => initial.diagnosticData?.value === row.diagnosticData?.value
               );
               const isReconfigure = row.command?.value === "RE_CONFIGURE";
               const commandChanged = !initialRow || initialRow.command?.value !== row.command?.value;
               return isReconfigure || commandChanged;
             })() && (
              <div>
                <div className="row odl-config-row" style={{ marginBottom: '10px' }}>
                  <div className="dropdown-section" style={{ width: '50.5%' }}>
                    <div className="dropdown-heading">
                      Max File Size (MB)<span className="required-asterisk">*</span>
                    </div>
                    <input
                      className="max-file-size-input"
                      type="number"
                      placeholder="Enter max file size in MB"
                      value={maxFileSizeMb}
                      onChange={(e) => setMaxFileSizeMb(e.target.value)}
                    />
                  </div>
                  {contextRules.length === 0 && (
                    <button className="add-row-button" style={{ alignSelf: 'flex-end', margin: '0' }} onClick={addContextRule}>
                      + Add Context Collection Rule
                    </button>
                  )}
                </div>

                {contextRules.map((ruleEntry, ruleIdx) => {
                  const usedRuleValues = new Set(
                    contextRules
                      .filter((_, i) => i !== ruleIdx)
                      .map(r => r.contextCollectionRule?.value)
                      .filter(Boolean)
                  );
                  const availableRuleOptions = contextCollectionRuleOptions.filter(
                    opt => !usedRuleValues.has(opt.value)
                  );
                  return (
                  <div className="row" key={ruleIdx} style={{ marginBottom: '2px' }}>
                    <div className="dropdown-section">
                      <div className="dropdown-heading">Context Collection Rule</div>
                      <Select
                        className="dropdown multi-select"
                        styles={selectStyles}
                        options={availableRuleOptions}
                        value={ruleEntry.contextCollectionRule}
                        onChange={(val) => handleContextRuleChange(ruleIdx, 'contextCollectionRule', val)}
                        placeholder="Select..."
                      />
                    </div>
                    <div className="dropdown-section">
                      <div className="dropdown-heading">Log Mask</div>
                      <Select
                        className="dropdown multi-select"
                        styles={selectStyles}
                        options={logMaskDescriptions}
                        value={ruleEntry.diagConfig}
                        onChange={(val) => handleContextRuleChange(ruleIdx, 'diagConfig', val)}
                        placeholder="Select..."
                      />
                    </div>
                    <button className="add-row-button" style={{ alignSelf: 'flex-end', margin: '0' }} onClick={addContextRule}>
                      + Add Context Collection Rule
                    </button>
                    <button className="add-row-button" style={{ alignSelf: 'flex-end', margin: '0' }} onClick={() => removeContextRule(ruleIdx)}>
                      - Remove Rule
                    </button>
                  </div>
                  );
                })}

                {contextRules.length > 0 && (
                  <div style={{ marginTop: '16px', marginBottom: '20px', padding: '15px', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                    <div className="dropdown-heading" style={{ marginBottom: '12px', fontWeight: '600' }}>
                      Collection Schedule (Optional)
                    </div>
                    <div className="row" style={{ marginBottom: '10px' }}>
                      <div className="dropdown-section">
                        <div className="dropdown-heading">Apply to Rules</div>
                        <Select
                          className="dropdown multi-select"
                          styles={selectStylesMulti}
                          isMulti
                          options={scheduleRuleOptions}
                          value={collectionSchedule.applyToRules}
                          onChange={(vals) => handleScheduleChange('applyToRules', vals || [])}
                          placeholder="Select rules..."
                        />
                      </div>
                      <div className="dropdown-section">
                        <div className="dropdown-heading">Start Time (UTC)</div>
                        <input
                          type="time"
                          value={collectionSchedule.startTime}
                          onChange={(e) => handleScheduleChange('startTime', e.target.value)}
                        />
                      </div>
                      <div className="dropdown-section">
                        <div className="dropdown-heading">Duration (mins)</div>
                        <input
                          type="number"
                          placeholder="e.g. 30"
                          value={collectionSchedule.durationMins}
                          onChange={(e) => handleScheduleChange('durationMins', e.target.value)}
                        />
                      </div>
                      <div className="dropdown-section">
                        <div className="dropdown-heading">Window Duration (mins)</div>
                        <input
                          type="number"
                          placeholder="e.g. 60"
                          value={collectionSchedule.windowDuration}
                          onChange={(e) => handleScheduleChange('windowDuration', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="row" style={{ marginBottom: '0' }}>
                      <div className="dropdown-section">
                        <div className="dropdown-heading">Active Days</div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
                          {DAYS.map(day => (
                            <label key={day.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '14px' }}>
                              <input
                                type="checkbox"
                                checked={collectionSchedule.activeDays[day.key]}
                                onChange={(e) => handleScheduleChange('activeDays', {
                                  ...collectionSchedule.activeDays,
                                  [day.key]: e.target.checked
                                })}
                              />
                              {day.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* V1 devices with CONFIGURE_ENABLE or RE_CONFIGURE: show legacy Max File Size only if changed */}
            {row.diagnosticData?.label === "ODL" &&
             isOpenEndedDisabled &&  // V1 device check
             (row.command?.value === "CONFIGURE_ENABLE" || row.command?.value === "RE_CONFIGURE") &&
             (() => {
               const initialRow = initialRows.find(
                 initial => initial.diagnosticData?.value === row.diagnosticData?.value
               );
               const isReconfigure = row.command?.value === "RE_CONFIGURE";
               const commandChanged = !initialRow || initialRow.command?.value !== row.command?.value;
               return isReconfigure || commandChanged;
             })() && (
              <div className="row odl-config-row">
                <div className="dropdown-section">
                  <div className="dropdown-heading">
                    Max File Size (MB)<span className="required-asterisk">*</span>
                  </div>
                  <input
                    className="max-file-size-input"
                    type="number"
                    placeholder="Enter max file size"
                    value={row.maxFileSizeMb}
                    onChange={(e) => handleRowChange(index, "maxFileSizeMb", e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="submit-button-section">
          <button
            className="submit-button"
            onClick={handleContinue}
            disabled={!hasChanges || !isFormValid()}
          >
            Continue
          </button>
          <button className="go-back-button" onClick={onGoBack}>
            {'<'} Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Review Page
  return (
    <div className="content-tab-container">
      <div className="tab-heading">
        <h1>Update Remote Diagnostic Session</h1>
      </div>

      <div className="review-section">
        <h3>Review your selection:</h3>

        <div className="review-cards">
          <div className="review-card">
            <h4>Session Details</h4>
            <div className="review-card-content">
              <div className="review-item">
                <span className="review-label">System ID:</span>
                <span className="review-value">{session.systemId}</span>
              </div>
              <div className="review-item">
                <span className="review-label">Session ID:</span>
                <span className="review-value">{session.sessionId}</span>
              </div>
            </div>
          </div>

          <div className="review-card">
            <h4>Diagnostic Data Changes</h4>
            <div className="review-card-content">
              {(() => {
                const allDiagnosticDataLabels = new Set([
                  ...initialRows.map(row => row.diagnosticData?.label),
                  ...rows.map(row => row.diagnosticData?.label)
                ]);

                const reviewTableData = Array.from(allDiagnosticDataLabels)
                  .filter(label => label !== undefined)
                  .map(label => {
                    const initialItem = initialRows.find(item => item.diagnosticData?.label === label);
                    const currentItem = rows.find(item => item.diagnosticData?.label === label);
                    return {
                      label,
                      previousCommand: initialItem?.command?.label || "",
                      currentCommand: currentItem?.command?.label || "",
                    };
                  });

                return (
                  <table className="review-table">
                    <thead>
                      <tr>
                        <th>Diagnostic Data</th>
                        <th>Previous Command</th>
                        <th>Current Command</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewTableData.length > 0 ? (
                        reviewTableData.map((data, index) => (
                          <tr key={index}>
                            <td>{data.label}</td>
                            <td>{data.previousCommand}</td>
                            <td>{data.currentCommand}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3">No diagnostic data to review.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {successMessage && (
        <div className="success-banner">
          <div className="success-message">{successMessage}</div>
          <button className="submit-button" onClick={onGoBack}>
            {'<'} Back to Sessions
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="error-banner">
          <div className="error-message">{errorMessage}</div>
        </div>
      )}

      {!successMessage && (
        <div className="review-actions">
          <button className="go-back-button" onClick={handleGoBack}>
            {'<'} Go Back
          </button>
          <button className="submit-button" onClick={handleSubmit}>
            {errorMessage ? 'Try Again' : 'Submit Update'}
          </button>
        </div>
      )}
    </div>
  );
}

export default UpdateSessionComponent;
